import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Clock, FileWarning, Upload, Loader2, AlertTriangle, IdCard } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDocumentMeta } from "@/lib/seo";
import { PartnerTerritorySection } from "@/components/partner-portal/PartnerTerritorySection";
import { PartnerDashboardSection } from "@/components/partner-portal/PartnerDashboardSection";
import { PartnerJourneyStepper, PartnerTerritoryBanner } from "@/components/partner-portal/PartnerJourneyStepper";

const PETROLE = "#284E55";

const SITUATION_LABELS = {
  installe: "Déjà installé / entreprise existante",
  en_creation: "Entreprise en cours de création",
  a_creer: "Souhaite créer son activité",
};

const DOCUMENT_LABELS = { identity: "Pièce d'identité", kbis: "KBIS", rc_pro: "Attestation RC Professionnelle" };

const NON_BLOCKING_NOTE = {
  kbis: "À fournir lorsque votre entreprise sera immatriculée.",
  rc_pro: "À fournir avant l'activation définitive de votre partenariat.",
};

const STATUS_META = {
  a_fournir: { label: "À fournir", cls: "bg-slate-100 text-slate-500", icon: Clock },
  envoye: { label: "Document envoyé", cls: "bg-sky-100 text-sky-700", icon: Check },
  valide: { label: "Validé", cls: "bg-sage/20 text-navy", icon: Check },
  refuse: { label: "Refusé", cls: "bg-red-100 text-red-600", icon: FileWarning },
};

const DocumentRow = ({ docType, doc, onUpload, uploading }) => {
  const fileRef = useRef(null);
  const isBlocking = doc.blocking;
  const displayStatus = !isBlocking && doc.status === "a_fournir" ? "a_completer" : doc.status;
  const meta = displayStatus === "a_completer" ? { label: "À compléter", cls: "bg-amber-50 text-amber-700", icon: Clock } : STATUS_META[doc.status];
  const Icon = meta.icon;
  const locked = doc.status === "valide";

  return (
    <div data-testid={`portal-document-${docType}`} className="rounded-2xl border-2 border-[#EDEAE3] bg-white p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-navy">{DOCUMENT_LABELS[docType]}</p>
          {!isBlocking && !doc.filename && (
            <p className="mt-1 text-xs leading-relaxed text-stone-500">{NON_BLOCKING_NOTE[docType]}</p>
          )}
          {doc.filename && <p className="mt-1 text-xs text-stone-400">{doc.filename}</p>}
        </div>
        <span data-testid={`portal-document-status-${docType}`} className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ${meta.cls}`}>
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </span>
      </div>

      {doc.status === "refuse" && doc.rejection_reason && (
        <p data-testid={`portal-document-reason-${docType}`} className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-xs leading-relaxed text-red-600">
          Motif du refus : {doc.rejection_reason}
        </p>
      )}

      {!locked && (
        <div className="mt-4">
          <input
            ref={fileRef}
            data-testid={`portal-document-input-${docType}`}
            type="file"
            accept="application/pdf,image/jpeg,image/png"
            capture="environment"
            className="hidden"
            onChange={(e) => onUpload(docType, e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid={`portal-document-upload-${docType}`}
            onClick={() => fileRef.current?.click()}
            disabled={uploading === docType}
            className="flex items-center gap-2 rounded-full border-2 border-[#EDEAE3] px-5 py-2.5 text-sm font-bold text-navy transition-colors duration-300 hover:border-[#284E55] disabled:opacity-60"
          >
            {uploading === docType ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {doc.status === "a_fournir" ? "Envoyer le document" : "Remplacer le document"}
          </button>
        </div>
      )}
    </div>
  );
};

export default function PartnerPortalPage() {
  const { token } = useParams();
  const [portal, setPortal] = useState(null);
  const [territory, setTerritory] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const documentsRef = useRef(null);
  const partnerStatus = territory && territory.activation_status !== "CANDIDATURE" ? territory.activation_status : null;

  useDocumentMeta("Espace Partenaire sécurisé | CozyHome", "Espace sécurisé Partenaire CozyHome : documents, territoire et suivi des projets.");

  const fetchPortal = useCallback(() => {
    api.get(`/partner-portal/${token}`)
      .then((res) => { setPortal(res.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const fetchTerritory = useCallback(() => {
    api.get(`/partner-portal/${token}/territory`).then((res) => setTerritory(res.data)).catch(() => {});
  }, [token]);

  useEffect(() => { fetchPortal(); }, [fetchPortal]);
  useEffect(() => { fetchTerritory(); }, [fetchTerritory]);

  const handleUpload = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/partner-portal/${token}/documents/${docType}`, fd);
      toast.success("Document reçu");
      fetchPortal();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(null);
    }
  };

  const refreshAfterTerritoryChoice = () => {
    fetchPortal();
    fetchTerritory();
  };

  return (
    <div className="App">
      <Navbar />
      <section className="min-h-[70vh] bg-ivory pb-24 pt-32 sm:pb-32 sm:pt-36">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" style={{ color: PETROLE }} /></div>
          ) : error ? (
            <div data-testid="portal-invalid-link" className="rounded-3xl bg-white p-10 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
                <AlertTriangle className="h-6 w-6" />
              </span>
              <h1 className="mt-6 font-display text-2xl font-medium text-navy sm:text-3xl">Lien invalide ou expiré</h1>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone-500">
                Ce lien sécurisé n'est plus valide. Contactez CozyHome pour recevoir un nouveau lien vers votre
                espace de complétion de dossier.
              </p>
            </div>
          ) : (
            <div data-testid="portal-content">
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>Espace Partenaire sécurisé</p>
              <h1 data-testid="portal-greeting" className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">
                Bonjour {portal.firstname}, suivez votre parcours CozyHome
              </h1>
              <p className="mt-3 flex items-center gap-2 text-sm text-stone-500">
                <IdCard className="h-4 w-4" style={{ color: PETROLE }} />
                Situation professionnelle : <span className="font-semibold text-navy">{SITUATION_LABELS[portal.situation]}</span>
              </p>

              <PartnerTerritoryBanner territory={territory} />
              <PartnerJourneyStepper
                appStatus={portal.status}
                documents={portal.documents}
                partnerStatus={partnerStatus}
                onGoToDocuments={() => documentsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              />

              {portal.situation !== "installe" && (
                <p data-testid="portal-non-blocking-note" className="mt-6 rounded-2xl p-5 text-sm leading-relaxed" style={{ backgroundColor: `${PETROLE}0D`, color: PETROLE }}>
                  Vous pourrez compléter ces éléments lorsque votre entreprise sera créée. Ils seront nécessaires
                  avant l'activation définitive de votre partenariat CozyHome.
                </p>
              )}

              <div ref={documentsRef} data-testid="portal-documents-section" className="mt-8 space-y-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-stone-400">Dossier professionnel</p>
                {Object.entries(portal.documents).map(([docType, doc]) => (
                  <DocumentRow key={docType} docType={docType} doc={doc} onUpload={handleUpload} uploading={uploading} />
                ))}
              </div>

              {partnerStatus && partnerStatus !== "ACTIF" && territory && (
                <PartnerTerritorySection token={token} territory={territory} onUpdated={refreshAfterTerritoryChoice} />
              )}

              {partnerStatus === "SUSPENDU" && (
                <p data-testid="portal-suspended-banner" className="mt-8 rounded-2xl bg-amber-50 p-5 text-sm leading-relaxed text-amber-700">
                  Votre partenariat CozyHome est actuellement suspendu. Contactez la Centrale pour plus d'informations.
                </p>
              )}
              {partnerStatus === "RESILIE" && (
                <p data-testid="portal-resiliated-banner" className="mt-8 rounded-2xl bg-red-50 p-5 text-sm leading-relaxed text-red-600">
                  Votre partenariat CozyHome a été résilié.
                </p>
              )}

              {["ESPACE_ACTIF_TERRITOIRE_EN_ATTENTE", "ACTIF"].includes(partnerStatus) && <PartnerDashboardSection token={token} />}

              <p className="mt-8 text-xs leading-relaxed text-stone-400">
                Aucun paiement n'est demandé via ce formulaire. Vous pouvez revenir sur cette page tant que le lien reste valide.
              </p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
